import React from "react";
import { CustomButton, CustomCheckbox } from "../../../components";
import { CGST_RATE, SGST_RATE } from "../../../services/Api";
import styles from "./Pricing.module.css";

const parseAmount = (value = "") =>
  Number(String(value).replace(/[^0-9]/g, "")) || 0;

const formatAmount = (value) => `₹${Number(value).toLocaleString("en-IN")}`;

const BillingSummary = ({
  plan,
  slab,
  addOns = [],
  selectedAddOnIds = [],
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  appendTotalToPrimaryLabel = false,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  showAgreement = false,
  agreementChecked = false,
  onAgreementChange,
  showSecureBadge = false,
}) => {
  const selectedAddOns = addOns.filter((addOn) =>
    selectedAddOnIds.includes(addOn.id),
  );

  const planAmount = parseAmount(slab?.price);
  const addOnsAmount = selectedAddOns.reduce(
    (sum, addOn) => sum + parseAmount(addOn.price),
    0,
  );
  const subtotal = planAmount + addOnsAmount;
  const cgstAmount = Math.round((subtotal * CGST_RATE) / 100);
  const sgstAmount = Math.round((subtotal * SGST_RATE) / 100);
  const gstAmount = cgstAmount + sgstAmount;
  const totalPayable = subtotal + gstAmount;

  return (
    <div className={styles.billingCard}>
      <h3 className={styles.billingTitle}>Billing Summary</h3>

      <div className={styles.billingRow}>
        <span className={styles.billingLabel}>Plan</span>
        <span className={styles.billingValue}>{plan?.planName}</span>
      </div>
      <div className={styles.billingRow}>
        <span className={styles.billingLabel}>Price</span>
        <span className={styles.billingValue}>{formatAmount(planAmount)}</span>
      </div>

      {selectedAddOns.map((addOn) => (
        <div key={addOn.id} className={styles.billingRow}>
          <span className={styles.billingLabel}>Add-on For {addOn.name}</span>
          <span className={styles.billingValue}>
            {formatAmount(addOn.price)}
          </span>
        </div>
      ))}

      <div className={styles.billingRow}>
        <span className={styles.billingLabel}>CGST @ {CGST_RATE}%</span>
        <span className={styles.billingValue}>{formatAmount(cgstAmount)}</span>
      </div>
      <div className={styles.billingRow}>
        <span className={styles.billingLabel}>SGST @ {SGST_RATE}%</span>
        <span className={styles.billingValue}>{formatAmount(sgstAmount)}</span>
      </div>

      <div className={styles.billingDivider} />

      <div className={styles.billingTotalRow}>
        <span>Total Payable</span>
        <span className={styles.billingTotalValue}>
          {formatAmount(totalPayable)}
        </span>
      </div>

      {showAgreement && (
        <div className={styles.billingAgreement}>
          <CustomCheckbox
            checked={agreementChecked}
            onChange={(e) => onAgreementChange?.(e.target.checked)}
            label={
              <span className={styles.billingAgreementText}>
                By signing up, you have read and agree to our{" "}
                <a href="/privacy-policy" className={styles.billingLink}>
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/privacy-policy" className={styles.billingLink}>
                  Terms &amp; Conditions
                </a>
                .
              </span>
            }
          />
        </div>
      )}

      {showSecureBadge && (
        <p className={styles.billingSecureBadge}>
          🔒 Secured by Razorpay • 256-bit SSL
        </p>
      )}

      {primaryLabel && (
        <CustomButton
          variant="primary"
          rounded
          className={styles.proceedBtn}
          disabled={primaryDisabled || primaryLoading}
          loading={primaryLoading}
          onClick={onPrimary}
        >
          {appendTotalToPrimaryLabel
            ? `${primaryLabel} ${formatAmount(totalPayable)}`
            : primaryLabel}
        </CustomButton>
      )}

      {secondaryLabel && (
        <CustomButton
          variant="outline-primary"
          rounded
          className={`${styles.outlineBtn} ${styles.billingBackBtn}`}
          disabled={secondaryDisabled}
          onClick={onSecondary}
        >
          {secondaryLabel}
        </CustomButton>
      )}
    </div>
  );
};

export default BillingSummary;
